import { loadVidstack, useBundledHls } from "./vidstack";
import { BP } from "./breakpoints";
import { isHlsSrc, isBunnySrc, mediaLoadEvent, bunnyMp4Candidates, setVideoSources } from "./videoKind";
import {
  mediaCreated,
  mediaLoaded,
  mediaPlaying,
  mediaPaused,
  mediaDestroyed,
} from "./mediaTelemetry";

// Pooled <video>/<media-player> elements for product-card media.
//
// The product card (views/components/global/product-card.twig) never knows where
// it is used (100+ item shop archive, keen-slider carousels, collection grid,
// 404 decorative grid, mobile fallback lists). It only emits declarative slots:
//   .c-product-card__media-default / .c-product-card__media-hover, each either a
//   background-image <div> (image) or an empty slot carrying
//   data-card-media-type="video" data-src="<url>" (mp4 / webm / m3u8).
//
// A global pool of real elements is borrowed into the slot of a card while it
// is on screen (and into a hover slot while hovered), then returned — src
// cleared, decoder released — when the card scrolls away / unhovers. Elements
// are kept around and reused across borrows instead of being recreated each
// time, but the pool grows on demand: every video that is in the viewport
// gets a player, uncapped.
//
// Two element kinds, picked per record from its src (see videoKind.js):
// HLS (.m3u8) needs Vidstack's bundled hls.js, everything else (mp4/webm)
// plays fine as a plain <video> — lighter, no custom element to register.
// The two are pooled separately since a <video> can't be handed an HLS src.
// Bunny Stream videos (HLS or MP4) always go through a plain <video> with the
// MP4 rendition sized to the card (videoKind.bunnyMp4Candidates); only if none
// of the renditions loads does that slot fall back to HLS via Vidstack.
//
// Default (autoplay-on-view) videos use two intersection thresholds so the
// player is already created and loaded by the time it's actually visible:
//   - "preloading" (PRELOAD_MARGIN outside the viewport on any side) — a
//     player is borrowed and given its src, but stays paused.
//   - "visible" (the real viewport, 0 margin) — playback actually starts.
// Hover videos are unrelated to scroll position: they're borrowed on
// mouseenter and released on mouseleave, never preloaded ahead of time.
//
// Lifecycle (created/loaded/playing/paused) reports through the shared
// src/js/utils/mediaTelemetry.js instead of logging locally.

// Which pooled element a src needs: Vidstack only for non-Bunny HLS, or for a
// Bunny playlist none of whose MP4 renditions loaded.
function playerKind(src, forceHls) {
  if (forceHls) return "player";
  return isHlsSrc(src) && !isBunnySrc(src) ? "player" : "video";
}

/* -------------------------------------------------------------------------- */
/* PlayerPool — shared element pool                                            */
/* -------------------------------------------------------------------------- */

let holder = null;
const freeLists = { video: [], player: [] };

function getHolder() {
  if (!holder) {
    holder = document.createElement("div");
    holder.setAttribute("aria-hidden", "true");
    holder.style.cssText =
      "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;";
    document.body.appendChild(holder);
  }
  return holder;
}

async function growPlayer() {
  await loadVidstack();
  const player = document.createElement("media-player");
  player.className = "c-product-card__player";
  player.setAttribute("muted", "");
  player.setAttribute("loop", "");
  player.setAttribute("playsinline", "");
  player.setAttribute("load", "eager");
  player.innerHTML = "<media-provider></media-provider>";
  useBundledHls(player); // must be wired before any src is assigned
  getHolder().appendChild(player);
  freeLists.player.push(player);
}

function growVideo() {
  const video = document.createElement("video");
  video.className = "c-product-card__player";
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.setAttribute("muted", "");
  video.setAttribute("playsinline", "");
  video.preload = "auto";
  getHolder().appendChild(video);
  freeLists.video.push(video);
}

export const PlayerPool = {
  // kind: "player" (Vidstack, for HLS) or "video" (plain <video>) — see
  // videoKind.isHlsSrc, which every caller checks before acquiring.
  async acquire(kind) {
    const list = freeLists[kind];
    let el = list.pop();
    if (!el) {
      if (kind === "player") await growPlayer();
      else growVideo();
      el = list.pop();
    }
    return el || null;
  },

  release(el) {
    if (!el) return;
    const kind = el.tagName === "MEDIA-PLAYER" ? "player" : "video";
    try {
      el.pause?.();
    } catch {
      /* not ready */
    }
    try {
      el.src = ""; // fires provider-change(null)/emptied -> decoder / hls.js freed
    } catch {
      /* not ready */
    }
    el.removeAttribute("poster"); // don't leak the previous card's thumbnail into the next one
    getHolder().appendChild(el);
    const list = freeLists[kind];
    if (!list.includes(el)) list.push(el);
  },
};

/* -------------------------------------------------------------------------- */
/* PlaybackBudget — caps simultaneous autoplay on touch devices                */
/* -------------------------------------------------------------------------- */

// Every autoplaying video that should be playing (on screen, not hovered away)
// asks the budget with want(); on tablet/mobile only the TOUCH_MAX_PLAYING
// nearest the viewport centre actually play, the others stay paused on their
// current frame. Decoders and memory are what's limited on phones (too many
// at once: software decoding, battery drain, iOS reloading the tab). Hover
// videos are left out: the user asked for them. Desktop: no cap.
//
// Callers own their players: `play`/`pause` are only called on a change of
// state, and drop() never pauses (the caller pauses or releases right after).

const TOUCH_MAX_PLAYING = 6;
const touchQuery = window.matchMedia(BP.tabletAndMobile);
const RECHECK_MS = 500; // re-rank while over the cap (autoscroll moves cards)

export const PlaybackBudget = {
  entries: new Map(), // key -> { el, play, pause, playing }
  raf: 0,
  timer: 0,

  want(key, { el, play, pause }) {
    const entry = this.entries.get(key);
    if (entry) Object.assign(entry, { el, play, pause });
    else this.entries.set(key, { el, play, pause, playing: false });
    this.schedule();
  },

  drop(key) {
    if (this.entries.delete(key)) this.schedule();
  },

  schedule() {
    if (!this.raf) this.raf = requestAnimationFrame(() => this.update());
  },

  update() {
    this.raf = 0;
    const cap = touchQuery.matches ? TOUCH_MAX_PLAYING : Infinity;
    let list = [...this.entries.values()];

    if (list.length > cap) {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      for (const e of list) {
        const r = e.el.getBoundingClientRect();
        e.dist = Math.hypot(r.left + r.width / 2 - cx, r.top + r.height / 2 - cy);
      }
      list = list.sort((a, b) => a.dist - b.dist);
    }

    list.forEach((e, i) => {
      const shouldPlay = i < cap;
      if (shouldPlay === e.playing) return;
      e.playing = shouldPlay;
      if (shouldPlay) e.play();
      else e.pause();
    });

    // Distances change with scroll and autoscroll: keep re-ranking, only
    // while there's actually more candidates than slots.
    clearTimeout(this.timer);
    if (list.length > cap) this.timer = setTimeout(() => this.schedule(), RECHECK_MS);
  },
};

// No scroll listener: which cards are on screen already comes through want()/
// drop() (IntersectionObserver), and the RECHECK_MS timer re-ranks while over
// the cap. Re-ranking on every scroll frame read every entry's rect each frame.
touchQuery.addEventListener("change", () => PlaybackBudget.schedule());

/* -------------------------------------------------------------------------- */
/* CardMediaController — one per usage context                                 */
/* -------------------------------------------------------------------------- */

const hoverQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
const PRELOAD_MARGIN = "10%"; // all four sides — applies on both x and y scroll

export class CardMediaController {
  constructor({
    preloadMargin = PRELOAD_MARGIN,
    root = null,
    hover = true,
    rank = "viewport-distance",
    context = "product-card",
  } = {}) {
    this.rank = rank;
    this.hoverEnabled = hover;
    this.context = context;

    this.records = new Map(); // card <a> element -> record
    this._byBox = new Map(); // .c-product-card__media -> record

    this._destroyed = false;
    this._running = false;
    this._rerun = false;
    this._rafId = null;

    // Wide margin: enters this region -> borrow a player + set src (not playing yet).
    this.preloadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const record = this._byBox.get(entry.target);
          if (record) record.preloading = entry.isIntersecting;
        }
        this._scheduleRebalance();
      },
      { root, rootMargin: preloadMargin, threshold: 0 },
    );

    // Exact viewport: only this gates actual play()/pause().
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const record = this._byBox.get(entry.target);
          if (record) record.visible = entry.isIntersecting;
        }
        this._scheduleRebalance();
      },
      { root, rootMargin: "0px", threshold: 0 },
    );

    this._onEnter = this._onEnter.bind(this);
    this._onLeave = this._onLeave.bind(this);
  }

  /* ---- public API ---- */

  add(cards) {
    for (const el of Array.from(cards)) {
      if (this.records.has(el)) continue;

      const mediaBox = el.querySelector(".c-product-card__media");
      if (!mediaBox) continue;

      const defaultSlot = el.querySelector(".c-product-card__media-default");
      const hoverSlot = el.querySelector(".c-product-card__media-hover");
      const defaultVideo = defaultSlot?.dataset.cardMediaType === "video";
      const hoverVideo = hoverSlot?.dataset.cardMediaType === "video";

      // Pure image / image-image cards need no orchestration (CSS opacity cut).
      if (!defaultVideo && !hoverVideo) continue;

      const record = {
        el,
        defaultForceHls: false,
        hoverForceHls: false,
        mediaBox,
        defaultSlot,
        hoverSlot,
        defaultSrc: defaultVideo ? defaultSlot.dataset.src : null,
        hoverSrc: hoverVideo ? hoverSlot.dataset.src : null,
        defaultType: defaultVideo ? "video" : "image",
        hoverType: hoverVideo ? "video" : hoverSlot ? "image" : null,
        player: null,
        hoverPlayer: null,
        defaultMediaId: null,
        hoverMediaId: null,
        preloading: false,
        visible: false,
        hovering: false,
      };

      this.records.set(el, record);
      this._byBox.set(mediaBox, record);
      el.dataset.cardMediaOwned = "";
      this.preloadObserver.observe(mediaBox);
      this.visibilityObserver.observe(mediaBox);

      if (this.hoverEnabled) {
        el.addEventListener("mouseenter", this._onEnter);
        el.addEventListener("mouseleave", this._onLeave);
      }
    }
    // Sizes read here in one batch (one layout at most) for the MP4 rendition
    // choice, rather than when a player is assigned, right after a DOM write.
    for (const record of this.records.values()) {
      if (record.boxSize) continue;
      record.boxSize = Math.max(record.mediaBox.offsetWidth, record.mediaBox.offsetHeight);
    }
    this._scheduleRebalance();
  }

  remove(cards) {
    for (const el of Array.from(cards)) this._removeOne(el);
    this._scheduleRebalance();
  }

  // Reconcile against a new card set (AJAX list swap, slider re-clone).
  reset(cards) {
    const next = new Set(Array.from(cards));
    for (const el of [...this.records.keys()]) {
      if (!next.has(el)) this._removeOne(el);
    }
    this.add(next);
  }

  // Re-rank now, e.g. after a client-side display:none filter.
  refresh() {
    this._scheduleRebalance();
  }

  destroy() {
    this._destroyed = true;
    this.preloadObserver.disconnect();
    this.visibilityObserver.disconnect();
    if (this._rafId) cancelAnimationFrame(this._rafId);
    for (const record of this.records.values()) {
      record.el.removeEventListener("mouseenter", this._onEnter);
      record.el.removeEventListener("mouseleave", this._onLeave);
      this._releasePlayer(record, "player");
      this._releasePlayer(record, "hoverPlayer");
    }
    this.records.clear();
    this._byBox.clear();
  }

  /* ---- internals ---- */

  _removeOne(el) {
    const record = this.records.get(el);
    if (!record) return;
    this.preloadObserver.unobserve(record.mediaBox);
    this.visibilityObserver.unobserve(record.mediaBox);
    el.removeEventListener("mouseenter", this._onEnter);
    el.removeEventListener("mouseleave", this._onLeave);
    this._releasePlayer(record, "player");
    this._releasePlayer(record, "hoverPlayer");
    delete el.dataset.cardMediaOwned;
    this._byBox.delete(record.mediaBox);
    this.records.delete(el);
  }

  _releasePlayer(record, key) {
    const player = record[key];
    if (!player) return;
    if (key === "player") PlaybackBudget.drop(record);
    record[key] = null;
    const idKey = key === "player" ? "defaultMediaId" : "hoverMediaId";
    mediaDestroyed(record[idKey]);
    record[idKey] = null;
    PlayerPool.release(player);
  }

  // Within PRELOAD_MARGIN of the viewport — should have a player + src, but
  // not necessarily be playing yet. No separate display:none check: a target
  // without a box never intersects, so the observer already clears
  // `preloading` for hidden cards (reading offsetParent here forced a layout
  // on every rebalance).
  _isLoadable(record) {
    return record.defaultType === "video" && record.preloading;
  }

  // Actually on screen — should be playing.
  _isPlayable(record) {
    return this._isLoadable(record) && record.visible;
  }

  _title(record) {
    return record.el.querySelector(".c-product-card__title")?.textContent?.trim();
  }

  _startPlayback(record, key) {
    const player = record[key];
    if (!player) return;
    const idKey = key === "player" ? "defaultMediaId" : "hoverMediaId";
    mediaPlaying(record[idKey]);
    this._play(player);
  }

  _stopPlayback(record, key) {
    const player = record[key];
    if (!player) return;
    const idKey = key === "player" ? "defaultMediaId" : "hoverMediaId";
    mediaPaused(record[idKey]);
    try {
      player.pause?.();
    } catch {
      /* not ready */
    }
  }

  // Reconciles the default player's play/pause state with _isPlayable — call
  // after any change to preloading/visible/hovering for a record. Playable
  // players go through the PlaybackBudget, which decides who actually plays.
  _syncPlayback(record) {
    if (!record.player || record.hovering) return;
    if (this._isPlayable(record)) {
      PlaybackBudget.want(record, {
        el: record.mediaBox,
        play: () => this._startPlayback(record, "player"),
        pause: () => this._stopPlayback(record, "player"),
      });
    } else {
      PlaybackBudget.drop(record);
      this._stopPlayback(record, "player");
    }
  }

  _scheduleRebalance() {
    if (this._destroyed || this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._rebalance();
    });
  }

  async _rebalance() {
    if (this._destroyed) return;
    if (this._running) {
      this._rerun = true;
      return;
    }
    this._running = true;

    try {
      const loadable = [...this.records.values()].filter((r) =>
        this._isLoadable(r),
      );

      // Ranking only orders acquisition below — every loadable record still
      // gets a player, it just decides who loads first when several enter
      // the preload margin at once.
      if (this.rank === "viewport-distance") {
        const vpCenterY = window.innerHeight / 2;
        const vpCenterX = window.innerWidth / 2;
        for (const r of loadable) {
          const rect = r.mediaBox.getBoundingClientRect();
          r._dist =
            Math.abs(rect.top + rect.height / 2 - vpCenterY) +
            0.25 * Math.abs(rect.left + rect.width / 2 - vpCenterX);
        }
        loadable.sort((a, b) => a._dist - b._dist);
      }

      const loadableSet = new Set(loadable);

      // Release phase — records that left the preload margin entirely, plus
      // stale players. A hovered card keeps its (paused) default player so
      // unhover can resume it.
      for (const r of this.records.values()) {
        if (r.player && !loadableSet.has(r) && !r.hovering) {
          this._releasePlayer(r, "player");
        }
      }

      // Acquire phase — sequential so nearest-to-center loads first.
      for (const r of loadable) {
        if (r.player) continue;
        const player = await PlayerPool.acquire(
          playerKind(r.defaultSrc, r.defaultForceHls),
        );
        if (this._destroyed) {
          if (player) PlayerPool.release(player);
          return;
        }
        if (!player) break;
        if (!this._isLoadable(r) || r.player) {
          PlayerPool.release(player);
          continue;
        }
        this._assignDefault(r, player);
      }

      // Play/pause phase — independent of the acquire loop above so a record
      // that already had a player still reacts to a visible/hovering change.
      for (const r of loadable) {
        if (r.player) this._syncPlayback(r);
      }
    } catch {
      // A failed Vidstack import / not-ready player shouldn't wedge the loop.
    } finally {
      this._running = false;
      if (this._rerun) {
        this._rerun = false;
        this._scheduleRebalance();
      }
    }
  }

  _assignDefault(record, player) {
    record.player = player;
    record.defaultSlot.appendChild(player);
    record.defaultMediaId = mediaCreated({
      context: this.context,
      kind: "default",
      title: this._title(record),
      src: record.defaultSrc,
    });
    player.addEventListener(
      mediaLoadEvent(player),
      () => mediaLoaded(record.defaultMediaId),
      { once: true },
    );
    // Bunny Stream default thumbnail (see product-card.twig): shows instantly and holds
    // until the player actually has a frame, instead of a blank box on a slow connection.
    if (record.defaultSlot.dataset.poster) player.setAttribute("poster", record.defaultSlot.dataset.poster);
    try {
      if (player.tagName === "VIDEO") {
        this._setVideoSrc(player, record.defaultSrc, record.boxSize || record.mediaBox, record.defaultSlot.dataset.renditions, () => {
          record.defaultForceHls = true;
          if (record.player === player) this._releasePlayer(record, "player");
          this._scheduleRebalance();
        });
      } else player.src = record.defaultSrc;
    } catch {
      /* not ready */
    }
    // Playback is decided by the play/pause phase in _rebalance right after.
  }

  // Bunny sources: sized MP4 renditions in order, then `onHlsFallback` if the
  // original was an HLS playlist and none of them loaded.
  _setVideoSrc(video, src, box, renditions, onHlsFallback) {
    const candidates = bunnyMp4Candidates(src, box, renditions);
    if (!candidates) {
      video.src = src;
      return;
    }
    // HLS only as a fallback for guessed renditions: known ones exist, so their
    // failing means the browser can't play the video, which HLS won't change.
    setVideoSources(video, candidates, isHlsSrc(src) && !renditions ? onHlsFallback : undefined);
  }

  _play(player) {
    const attempt = () => {
      try {
        const p = player.play?.();
        if (p?.catch) {
          p.catch(() => {
            // Media not ready yet — play as soon as it can.
            player.addEventListener(mediaLoadEvent(player), () => attempt(), {
              once: true,
            });
          });
        }
      } catch {
        player.addEventListener(mediaLoadEvent(player), () => attempt(), {
          once: true,
        });
      }
    };
    attempt();
  }

  /* ---- hover ---- */

  async _onEnter(e) {
    const record = this.records.get(e.currentTarget);
    if (!record) return;
    record.hovering = true;

    // Default is instantly cut away by the hover layer (no crossfade).
    if (record.hoverSlot) {
      PlaybackBudget.drop(record);
      this._stopPlayback(record, "player");
    }

    if (record.hoverType !== "video" || !hoverQuery.matches) {
      return;
    }

    // Already holding a hover player (e.g. re-entered right after leaving) —
    // just resume it, never stack a second one.
    if (record.hoverPlayer) {
      this._startPlayback(record, "hoverPlayer");
      return;
    }

    const player = await PlayerPool.acquire(
      playerKind(record.hoverSrc, record.hoverForceHls),
    );
    if (this._destroyed || !player) return;
    // State may have changed across the await.
    if (!record.hovering || record.hoverPlayer) {
      PlayerPool.release(player);
      return;
    }
    record.hoverPlayer = player;
    record.hoverSlot.appendChild(player);
    record.hoverMediaId = mediaCreated({
      context: this.context,
      kind: "hover",
      title: this._title(record),
      src: record.hoverSrc,
    });
    player.addEventListener(
      mediaLoadEvent(player),
      () => mediaLoaded(record.hoverMediaId),
      { once: true },
    );
    if (record.hoverSlot.dataset.poster) player.setAttribute("poster", record.hoverSlot.dataset.poster);
    try {
      if (player.tagName === "VIDEO") {
        // Next hover gets the HLS player (this one just keeps its poster).
        this._setVideoSrc(player, record.hoverSrc, record.boxSize || record.mediaBox, record.hoverSlot.dataset.renditions, () => {
          record.hoverForceHls = true;
        });
      } else player.src = record.hoverSrc;
    } catch {
      /* not ready */
    }
    this._startPlayback(record, "hoverPlayer");
  }

  _onLeave(e) {
    const record = this.records.get(e.currentTarget);
    if (!record) return;
    record.hovering = false;

    // No hover layer -> the default was never cut away, nothing to resume.
    if (!record.hoverSlot) return;

    if (record.hoverPlayer) this._releasePlayer(record, "hoverPlayer");
    this._syncPlayback(record);
    // Catches a default player that fell outside the preload margin while
    // this card was being hovered (release was held off during the hover).
    this._scheduleRebalance();
  }
}

/* -------------------------------------------------------------------------- */
/* mountCardMedia — catch-all for cards no context controller claimed          */
/* -------------------------------------------------------------------------- */

let catchAll = null;

export function mountCardMedia(root = document) {
  const orphans = [
    ...root.querySelectorAll('[data-component="product-card"]'),
  ].filter(
    (el) =>
      el.dataset.cardMediaOwned === undefined &&
      !el.closest('[data-component="not-found-bg"]'),
  );
  if (!orphans.length) return;

  if (!catchAll) {
    catchAll = new CardMediaController({
      hover: true,
      rank: "viewport-distance",
      context: "product-card:catch-all",
    });
  }
  catchAll.add(orphans);
}
